import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import ThemeToggle from './ThemeToggle';

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const HeaderAr = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'الرئيسية', href: '/ar' },
    { name: 'الخدمات', href: '/ar/services' },
    { name: 'المدونة', href: '/ar/blog' },
    { name: 'حاسبة الطاقة', href: '/ar/calculator' },
    { name: 'اتصل بنا', href: '/ar/contact' }
  ];

  const whatsappUrl = "https://wa.me/96891305426?text=%D9%85%D8%B1%D8%AD%D8%A8%D8%A7%D8%8C%20%D8%A3%D8%B1%D8%BA%D8%A8%20%D9%81%D9%8A%20%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D9%81%D8%B3%D8%A7%D8%B1%20%D8%B9%D9%86%20%D8%AA%D8%B1%D9%83%D9%8A%D8%A8%20%D8%A7%D9%84%D8%B7%D8%A7%D9%82%D8%A9%20%D8%A7%D9%84%D8%B4%D9%85%D8%B3%D9%8A%D8%A9";

  const isActive = (path) => location.pathname === path;

  // Switch to the equivalent English page
  const enPath = location.pathname === '/ar' ? '/' : location.pathname.replace(/^\/ar/, '');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 transition-colors duration-300">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <div className="hidden lg:flex items-center space-x-4 space-x-reverse">
            <Link to="/ar/contact">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                احصل على عرض سعر
              </Button>
            </Link>
            <ThemeToggle />
            <div className="flex items-center space-x-4 space-x-reverse text-sm text-gray-600 dark:text-gray-400 pl-4 border-l border-gray-200 dark:border-gray-700">
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 space-x-reverse hover:text-green-600 dark:hover:text-green-400 transition-colors"
                onClick={() => window.gtag?.('event', 'click', { event_category: 'contact', event_label: 'phone_call_click' })}
              >
                <span>91305426 968+</span>
                <WhatsAppIcon className="w-4 h-4" />
              </a>
            </div>
            <Link to={enPath} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-600 transition-colors" data-testid="lang-switch-en">
              EN
            </Link>
          </div>

          <nav className="hidden lg:flex items-center space-x-1 space-x-reverse">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(item.href)
                    ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30'
                    : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <Link to="/ar" className="flex items-center group">
            <img 
              src="https://customer-assets.emergentagent.com/job_solar-epc-oman/artifacts/746jwpae_ECOSUN%20%281%29.svg" 
              alt="شعار إيكوصن للطاقة المتجددة" 
              className="h-16 w-16 rounded-full transform group-hover:scale-105 transition-transform duration-300"
              width="64"
              height="64"
              loading="lazy"
              decoding="async"
            />
          </Link>

          <div className="lg:hidden flex items-center space-x-3 space-x-reverse">
            <ThemeToggle />
            <Link to={enPath} className="text-xs font-semibold px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">EN</Link>
            <button
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-gray-900 dark:text-gray-100" /> : <Menu className="w-6 h-6 text-gray-900 dark:text-gray-100" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 dark:border-gray-800 animate-in slide-in-from-top-2 duration-200 bg-white dark:bg-gray-900">
            <nav className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors text-right ${
                    isActive(item.href)
                      ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800 space-y-2">
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-end space-x-2 space-x-reverse px-4 py-2 text-sm text-green-600 dark:text-green-400"
              >
                <span>91305426 968+</span>
                <WhatsAppIcon className="w-4 h-4" />
              </a>
              <Link to="/ar/contact" onClick={() => setMobileMenuOpen(false)} className="block">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  احصل على عرض سعر
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default HeaderAr;
